import {
  Box,
  FormControlLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import { ResponseFormulaSubTypeEnum } from '@ulysses-inc/harami_api_client'
import type { FormikProps } from 'formik'
import { type ChangeEvent, type FC } from 'react'
import FormikErrorMessage from 'src/components/formikErrorMessage/FormikErrorMessage'
import { SubTypeText } from 'src/constants/responseNumber'
import type { FormulaRuleProps } from './EditResponseFormulaRuleDrawer'

const NormalValueRow: FC<{
  formikProps: FormikProps<FormulaRuleProps>
}> = ({
  formikProps: {
    values: { hasRule, subType, minimum, maximum, scale },
    handleChange,
    setFieldValue,
    errors,
    touched,
  },
}) => {
  const update = (key: keyof FormulaRuleProps) => (value: unknown) =>
    setFieldValue(key, value)

  const onChange = (_: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    if (!checked) {
      update('minimum')(undefined)
      update('maximum')(undefined)
      update('scale')(undefined)
      update('subType')(undefined)
    }
    update('hasRule')(checked)
  }

  return (
    <Stack>
      <FormControlLabel
        control={<Switch checked={hasRule} onChange={onChange} />}
        label="正常値を設定する"
        sx={{ mb: 1 }}
      />
      {hasRule && (
        <Stack direction="row" alignItems="center">
          {subType !== ResponseFormulaSubTypeEnum.LESS_THAN && (
            <Box sx={{ width: 90 }}>
              <TextField
                type="number"
                size="small"
                name="minimum"
                placeholder="0"
                value={minimum ?? ''}
                onChange={e =>
                  update('minimum')(
                    e.target.value === '' ? undefined : Number(e.target.value),
                  )
                }
                slotProps={{ htmlInput: { 'aria-label': '正常値の下限値' } }}
                fullWidth
              />
            </Box>
          )}
          {(!subType || subType === ResponseFormulaSubTypeEnum.BETWEEN) && (
            <Box sx={{ width: 35, textAlign: 'center' }}>
              <Typography>ー</Typography>
            </Box>
          )}
          {subType !== ResponseFormulaSubTypeEnum.GREATER_THAN && (
            <Box sx={{ width: 90 }}>
              <TextField
                type="number"
                size="small"
                name="maximum"
                placeholder="100"
                value={maximum ?? ''}
                onChange={e =>
                  update('maximum')(
                    e.target.value === '' ? undefined : Number(e.target.value),
                  )
                }
                slotProps={{ htmlInput: { 'aria-label': '正常値の上限値' } }}
                fullWidth
              />
            </Box>
          )}
          <Box sx={{ width: 90, ml: 2 }}>
            <TextField
              size="small"
              name="scale"
              placeholder="℃"
              value={scale ?? ''}
              onChange={handleChange}
              slotProps={{ htmlInput: { 'aria-label': '正常値の単位' } }}
              fullWidth
            />
          </Box>
          <Box sx={{ width: 90 }}>
            <Select<ResponseFormulaSubTypeEnum | ''>
              displayEmpty
              size="small"
              value={subType || ''}
              onChange={e => update('subType')(e.target.value || undefined)}
              inputProps={{ 'aria-label': '正常値の範囲指定' }}
              renderValue={value =>
                value === '' ? (
                  <Box component="span" sx={{ color: 'text.disabled' }}>
                    の間
                  </Box>
                ) : (
                  SubTypeText[value]
                )
              }
              fullWidth
            >
              <MenuItem
                value={ResponseFormulaSubTypeEnum.BETWEEN}
                data-testid="select-number-subtype-item"
              >
                {SubTypeText[ResponseFormulaSubTypeEnum.BETWEEN]}
              </MenuItem>
              <MenuItem
                value={ResponseFormulaSubTypeEnum.GREATER_THAN}
                data-testid="select-number-subtype-item"
              >
                {SubTypeText[ResponseFormulaSubTypeEnum.GREATER_THAN]}
              </MenuItem>
              <MenuItem
                value={ResponseFormulaSubTypeEnum.LESS_THAN}
                data-testid="select-number-subtype-item"
              >
                {SubTypeText[ResponseFormulaSubTypeEnum.LESS_THAN]}
              </MenuItem>
            </Select>
          </Box>
        </Stack>
      )}
      <Stack>
        <FormikErrorMessage name="subType" errors={errors} touched={touched} />
        <FormikErrorMessage name="maximum" errors={errors} touched={touched} />
        <FormikErrorMessage name="minimum" errors={errors} touched={touched} />
      </Stack>
    </Stack>
  )
}

export default NormalValueRow
